import React, { createContext, useState, useContext, useEffect, useRef, useCallback } from 'react';
import axiosInstance from '../utils/axiosInstance';
import { useAuth } from './AuthContext';

const ContentContext = createContext();

export function ContentProvider({ children }) {
    const { isAuthenticated } = useAuth();
    const [userData, setUser] = useState(null);
    const [addresses, setAddresses] = useState([]);
    const [banner, setBanners] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [saldoData, setSaldoData] = useState(null);
    const [topAgentData, setTopAgentData] = useState(null);
    const [DownlineData, setDownlineData] = useState(null);
    const [categories, setCategories] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [marketingKitData, setMarketingKitData] = useState(null);
    const [priceList, setPriceList] = useState([]);
    const [FAQs, setFAQs] = useState(null);
    const [WithdrawAmount, setWithdrawAmount] = useState(null);
    const [SupportKitData, setSupportKitData] = useState([]);
    const [isNewUser, setIsNewUser] = useState(false);
    const [isPasswordFilled, setisPasswordFilled] = useState(false);
    const [isUserApproved, setisUserApproved] = useState(false);
    const [BankData, setBankData] = useState(false);
    const [RequestWDStatus, setRequestWDStatus] = useState(null);
    const [Username, setUsername] = useState(false);
    const [failedAttempts, setFailedAttempts] = useState(0);
    const [HistoryData, setHistoryData] = useState(0);
    const [events, setEvents] = useState([]);
    const [Notifications, setNotifications] = useState();
    const [userStatus, setUserStatus] = useState(null);

    const previousSearchKeyword = useRef('');
    const previousSearchQuery = useRef('');
    const previousCategory = useRef('');

    useEffect(() => {
        if (isAuthenticated) {
            const fetchData = async () => {
                await Promise.all([
                    fetchUserData(),
                    fetchAddressData(),
                    fetchBannerData(),
                    fetchSaldoData(),
                    fetchTopAgentData(),
                    fetchDownlineData(),
                    fetchCategoriesData(),
                    fetchMarketingKitData(),
                    fetchPriceList(),
                    fetchFAQs(),
                    fetchSupportKitData(),
                    fetchBankData(),
                    fetchHistoryData(),
                    fetchScrapeEvents(),
                    fetchNotificationData()
                ]);
            };

            fetchData();
        }
    }, [isAuthenticated]);

    const fetchUserData = async () => {
        try {
            setLoading(true); // Start loading state
            console.log("Fetching user data from /api/user/show...");

            const response = await axiosInstance.get('/api/user/show');
            console.log("Response received:", response.data);

            if (response.data.status === 'success') {
                const user = response.data.data;
                console.log("User data:", user);

                const { phone, status, name } = user;

                // Set user status
                setUserStatus(status);

                // Handle status-specific logic
                if (status === 0) {
                    setError("Akun Anda tidak aktif. Mohon untuk melengkapi profil untuk aktivasi.");
                } else if (status === 1) {
                    setError("Akun Anda sedang menunggu persetujuan. Harap tunggu 1x24 jam.");
                } else if (status === 3) {
                    setError("Akun Anda telah diblokir. Silakan hubungi admin untuk informasi lebih lanjut.");
                } else if (status === 2) {
                    console.log("User status: Active");
                    setError(null); // Clear any existing errors
                } else {
                    setError("Status akun tidak valid.");
                }

                // Check for phone number
                if (!phone) {
                    const missingPhoneError = `Halo ${name || "Pengguna"}, Anda belum mengisi nomor telepon. Mohon lengkapi di halaman Profil.`;
                    console.error(missingPhoneError);
                    setError(missingPhoneError);
                }

                // Update user data state
                setUser(user);
                console.log("User data set successfully.");
                return phone; // Return phone number directly
            } else {
                setError(response.data.message || "Gagal mengambil data pengguna.");
            }
        } catch (err) {
            console.error("Error in fetchUserData:", err.message);
            setError(err.message); // Update the error state
        } finally {
            setLoading(false); // End loading state
            console.log("fetchUserData process completed.");
        }

        return null; // Return null if phone is not available or there is an error
    };

    const updateUserData = async (data) => {
        try {
            setLoading(true);
            console.log('Raw data received:', data);

            // Convert data to FormData if necessary
            if (!(data instanceof FormData)) {
                const formData = new FormData();
                Object.keys(data).forEach(key => {
                    console.log(`Appending key: ${key}, value: ${data[key]}`);
                    formData.append(key, data[key]);
                });
                data = formData; // Reassign to FormData
            }

            console.log('FormData entries:');
            for (let [key, value] of data.entries()) {
                console.log(`${key}:`, value);
            }

            const response = await axiosInstance.post('/api/user/update', data, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });

            console.log('Full API Response:', response.data);

            if (response.data.status === 'success') {
                const userData = response.data.data || {}; // Fallback if `data` is undefined
                console.log('User details updated successfully:', userData);
                setUser(userData); // Update user state
            } else {
                console.error('API returned an error:', response.data.message || 'Failed to update user details');
                setError({ message: response.data.message || 'Failed to update user details' });
            }
        } catch (err) {
            console.error('Error during updateUserData:', err.message);

            if (err.response && err.response.data.status === 'error' && err.response.data.message) {
                const errorMessages = err.response.data.message;
                let formattedErrors = {};

                Object.keys(errorMessages).forEach(field => {
                    formattedErrors[field] = errorMessages[field].join(', '); // Join array of error messages
                });

                console.error('Field validation errors:', formattedErrors);
                setError({ message: formattedErrors });
            } else if (err.response) {
                console.error('API Error Response:', err.response);
                setError({ message: 'An unexpected error occurred on the server' });
            } else if (err.request) {
                console.error('API Error Request:', err.request);
                setError({ message: 'No response from server' });
            } else {
                console.error('Unknown error during updateUserData:', err);
                setError({ message: 'An unexpected error occurred' });
            }
        } finally {
            setLoading(false);
            console.log('Loading state set to false');
        }
    };


    const fetchAddressData = async () => {
        try {
            console.log("Fetching address data..."); // Log when the function starts
            setLoading(true);

            const response = await axiosInstance.get('/api/home/address');

            console.log("Response received:", response.data); // Log the entire response

            if (response.data.status) {
                console.log("Addresses fetched successfully:", response.data.data); // Log the fetched addresses
                setAddresses(response.data.data);
            } else {
                console.warn("Response status is false:", response.data); // Log unexpected status
            }
        } catch (err) {
            console.error("Error fetching address data:", err.message); // Log errors with details
            setError(err.message);
        } finally {
            console.log("Fetch address data completed."); // Log when the process is finished
            setLoading(false);
        }
    };

    const logoutUser = async () => {
        try {
            console.log("Attempting to log out..."); // Log when the process starts
            setLoading(true); // Indicate the loading state

            const response = await axiosInstance.get('/api/user/logout');

            console.log("Response received:", response.data); // Log the entire response

            if (response.data.status) {
                console.log("Logout successful."); // Log successful logout
                // Perform actions after a successful logout
                localStorage.removeItem('authToken'); // Remove the auth token
            } else {
                console.warn("Logout failed with status:", response.data); // Log unexpected response
            }
        } catch (err) {
            console.error("Error during logout:", err.message); // Log errors with details
            setError(err.message); // Store the error message
        } finally {
            console.log("Logout process completed."); // Log when the process is finished
            setLoading(false); // Reset the loading state
        }
    };


    const fetchNotificationData = async () => {
        try {
            console.log("Fetching notification data..."); // Log when the function starts
            setLoading(true); // Start the loading spinner

            const response = await axiosInstance.get('/api/notification');

            console.log("Response received:", response.data); // Log the entire response

            if (response.data.status) {
                console.log("Notifications fetched successfully:", response.data.data); // Log the fetched notifications
                setNotifications(response.data.data); // Update state with the data array
            } else {
                console.warn("Response status is false:", response.data.message); // Log the message in case of failure
            }
        } catch (err) {
            console.error("Error fetching notification data:", err.message); // Log the error
            setError(err.message); // Update state with the error message
        } finally {
            console.log("Fetch notification data completed."); // Log when the process finishes
            setLoading(false); // Stop the loading spinner
        }
    };

    const markNotificationAsRead = async (notifId) => {
        try {
            console.log(`Marking notification ${notifId} as read...`);
            const response = await axiosInstance.get(`/api/notification/read?notif_id=${notifId}`);

            console.log("Mark as read response:", response.data);

            if (response.data.status) {
                console.log(`Notification ${notifId} marked as read successfully.`);
                setNotifications((prevNotifications) =>
                    prevNotifications.map((notification) =>
                        notification.id === notifId
                            ? { ...notification, read: true } // Update the read status
                            : notification
                    )
                );
            } else {
                console.warn("Failed to mark notification as read:", response.data.message);
            }
        } catch (err) {
            console.error("Error marking notification as read:", err.message);
            setError(err.message);
        }
    };


    const fetchBannerData = async () => {
        try {
            setLoading(true);
            const response = await axiosInstance.get('/api/home/banner');
            if (response.data.status === 'success') {
                setBanners(response.data.data.top.concat(response.data.data.bottom));
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const fetchCategoriesData = async () => {
        try {
            setLoading(true); // Start loading before the request
            const response = await axiosInstance.get('/api/marketing-kit/category');

            if (response.data.status === 'success') {
                // Assuming you have a state variable like `setCategories` to hold the fetched categories
                setCategories(response.data.data); // Store the categories in the state
            }
        } catch (err) {
            setError(err.message); // Set any error that occurred during the request
        } finally {
            setLoading(false); // Ensure loading state is turned off after request completes
        }
    };


    const fetchSaldoData = async (startDate, endDate) => {
        try {
            setLoading(true);
            setError(null);

            const ref = `${encodeURIComponent(startDate)}_${encodeURIComponent(endDate)}`;
            console.log(`Fetching saldo data for: ${ref}`);

            const response = await axiosInstance.get(`/api/home?ref=${ref}`);
            console.log('API Response:', response.data); // Log the entire API response

            if (response.data.status) {
                setSaldoData(response.data.data);
            } else {
                throw new Error('Invalid response status');
            }
        } catch (err) {
            console.error('Error fetching saldo data:', err);  // Log the error in detail
            setError(err.response?.data?.message || err.message); // Capture specific API errors
        } finally {
            setLoading(false);
        }
    };

    const fetchTopAgentData = async (startDate, endDate) => {
        try {
            setLoading(true);
            setError(null);

            const ref = `${encodeURIComponent(startDate)}_${encodeURIComponent(endDate)}`;
            console.log(`Fetching top agent data for: ${ref}`);

            const response = await axiosInstance.get(`/api/home/top-agent?ref=${ref}`);
            console.log('API Response:', response.data); // Log the entire API response

            if (response.data.status) {
                setTopAgentData(response.data.data);
            } else {
                throw new Error('Invalid response status');
            }
        } catch (err) {
            console.error('Error fetching top agent data:', err); // Log the error in detail
            setError(err.response?.data?.message || err.message); // Capture specific API errors
        } finally {
            setLoading(false);
        }
    };

    const fetchDownlineData = async (startDate, endDate) => {
        try {
            setLoading(true);
            setError(null);

            const ref = `${encodeURIComponent(startDate)}_${encodeURIComponent(endDate)}`;
            console.log(`Fetching downline data for: ${ref}`);

            // Making the GET request to the API endpoint
            const response = await axiosInstance.get(`/api/home/downline?ref=${ref}`);
            console.log('API Response:', response.data); // Log the entire API response

            // Check if the response has a valid status
            if (response.data.status) {
                setDownlineData(response.data.data); // Assuming you have a state to store downline data
            } else {
                throw new Error('Invalid response status');
            }
        } catch (err) {
            console.error('Error fetching downline data:', err); // Log the error in detail
            setError(err.response?.data?.message || err.message); // Capture specific API errors
        } finally {
            setLoading(false);
        }
    };

    const fetchMarketingKitData = async (searchQuery) => {
        try {
            setLoading(true);
            setError(null);

            // Skip fetching if the search query hasn't changed
            if (searchQuery === previousSearchQuery.current) {
                console.log('No need to fetch marketing kit data. Query is unchanged.');
                return; // Skip API call if query hasn't changed
            }

            // Update previousSearchQuery
            previousSearchQuery.current = searchQuery;

            // Encode the search query if provided, otherwise set to an empty string
            const query = searchQuery ? `?search=${encodeURIComponent(searchQuery)}` : '';
            console.log(`Fetching marketing kit data for: ${query || ''}`);

            // Making the GET request to the API endpoint
            const response = await axiosInstance.get(`/api/marketing-kit${query}`);
            console.log('API Response:', response.data); // Log the entire API response

            // Check if the response has a valid status
            if (response.data.status) {
                setMarketingKitData(response.data.data); // Assuming you have a state to store marketing kit data
            } else {
                throw new Error('Invalid response status');
            }
        } catch (err) {
            console.error('Error fetching marketing kit data:', err); // Log the error in detail
            setError(err.response?.data?.message || err.message); // Capture specific API errors
        } finally {
            setLoading(false);
        }
    };

    const fetchSupportKitData = async (searchKeyword) => {
        try {
            console.log('Initiating fetchSupportData function'); // Log function start
            setLoading(true);
            setError(null);
            console.log('Loading state set to true and error reset'); // Log state changes

            // Check if the searchKeyword is unchanged and not empty
            if (searchKeyword !== undefined && searchKeyword === previousSearchKeyword.current) {
                console.log('No need to fetch support data. Keyword is unchanged:', searchKeyword); // Log skip condition
                return;
            }

            // Update previousSearchKeyword
            console.log('Updating previous search keyword from', previousSearchKeyword.current, 'to', searchKeyword); // Log keyword update
            previousSearchKeyword.current = searchKeyword;

            // Prepare query
            const query = searchKeyword ? `?keyword=${encodeURIComponent(searchKeyword)}` : '?keyword=';
            console.log(`Constructed query: ${query}`); // Log query construction

            // Log the final constructed URL
            console.log('Constructed API request URL:', `/api/support${query}`); // Log final URL

            // Make API request
            console.log('Sending GET request to API endpoint:', `/api/support${query}`);
            const response = await axiosInstance.get(`/api/support${query}`);
            console.log('API Response:', response.data); // Log the entire API response
            console.log('SupportKitData:', SupportKitData); // Inspect the fetched data

            // Process API response
            if (response.data.status) {
                console.log('Successful response. Updating support data state.'); // Log success case
                setSupportKitData(response.data.data);
            } else {
                console.error('API responded with invalid status.'); // Log invalid status
                throw new Error(response.data.message || 'Invalid response status');
            }
        } catch (err) {
            console.error('Error occurred during fetchSupportData:', err); // Log error in detail
            setError(err.response?.data?.message || err.message); // Capture specific API errors
        } finally {
            console.log('Setting loading state to false. Fetch process completed.'); // Log final step
            setLoading(false);
        }
    };



    const fetchPriceList = async (categoryId) => {
        if (!categoryId || categoryId === previousCategory.current) return; // Prevent fetch if category hasn't changed
        try {
            console.log(`Fetching price list for category ${categoryId}...`);
            setLoading(true);
            const response = await axiosInstance.get(`/api/price-list?category_id=${categoryId}`);
            console.log(`Response received for category ${categoryId}:`, response.data);

            if (response.data.status) {
                setPriceList(response.data.data);
                previousCategory.current = categoryId; // Update the previous category after fetching
            } else {
                console.warn(`Response status is false for category ${categoryId}:`, response.data);
            }
        } catch (err) {
            console.error(`Error fetching price list for category ${categoryId}:`, err.message);
            setError(err.message);
        } finally {
            setLoading(false);
            console.log("Fetch price list completed.");
        }
    };

    const fetchFAQs = async (categoryId) => {
        if (!categoryId || categoryId === previousCategory.current) return; // Prevent fetch if category hasn't changed

        try {
            console.log(`Fetching FAQs for category ${categoryId}...`);
            setLoading(true);
            setError(null); // Reset error state before new fetch

            // Making the GET request to the API endpoint
            const response = await axiosInstance.get(`/api/faq?category_id=${categoryId}`);
            console.log(`Response received for category ${categoryId}:`, response.data);

            if (response.data.status) {
                setFAQs(response.data.data); // Assuming `setFAQs` updates the FAQ data state
                previousCategory.current = categoryId; // Update the previous category after successful fetch
            } else {
                console.warn(`Response status is false for category ${categoryId}:`, response.data);
                throw new Error('Invalid response status');
            }
        } catch (err) {
            console.error(`Error fetching FAQs for category ${categoryId}:`, err.message);
            setError(err.response?.data?.message || err.message); // Set error message
        } finally {
            setLoading(false);
            console.log("Fetch FAQs completed.");
        }
    };

    const fetchBankData = useCallback(async () => {
        try {
            setLoading(true);  // Set loading state to true while fetching
            console.log('Fetching bank data...');

            // Fetch bank data from the API
            const response = await axiosInstance.get('/api/transaction/bank');
            console.log('API Response:', response);

            // Check if the response status is 204 (No Content)
            if (response.status === 204) {
                console.log('No bank details found (status 204)');
                // If 204, it indicates no bank details, so set isNewUser to true
                setIsNewUser(true);
            } else if (response.data.status === 'success') {
                console.log('Bank details fetched successfully:', response.data.data);
                // If successful, update the state with the fetched bank data
                setBankData(response.data.data);
                setIsNewUser(false);  // Set isNewUser to false since bank details exist
            } else {
                console.error('API returned an error:', response.data.message || 'Failed to fetch bank details');
                // Handle other API errors
                setError(response.data.message || 'Failed to fetch bank details');
            }
        } catch (err) {
            console.error('Error during fetchBankData:', err.message);
            // Catch any unexpected errors
            setError(err.message);
        } finally {
            // Set loading to false once the request is done
            console.log('Loading state set to false');
            setLoading(false);
        }
    }, []);


    const updateBankData = async (data) => {
        try {
            setLoading(true);  // Set loading state to true while updating
            console.log('Updating bank data with the following data:', data); // Log the data being passed

            // Use POST method to update bank details, pass the data to the API
            const response = await axiosInstance.post('/api/transaction/bank', data);
            console.log('Update Bank API Response:', response);

            if (response.data.status === 'success') {
                console.log('Bank details updated successfully:', response.data.data);
                setBankData(response.data.data);  // Update the user data in the state
                setIsNewUser(false);  // Mark as updated since bank details exist
            } else {
                console.error('API returned an error:', response.data.message || 'Failed to update bank details');
                setError({ message: response.data.message || 'Failed to update bank details' });
            }
        } catch (err) {
            console.error('Error during updateBankData:', err.message);
            setError({ message: err.message });  // Catch any unexpected errors and store them in error state
        } finally {
            setLoading(false);  // Set loading to false once the request is done
            console.log('Loading state set to false');
        }
    };

    const sendForgotPasswordBankRequest = async (username) => {
        try {
            console.log("Sending forgot password request to /api/auth/forgot...");
            const payload = {
                username,
                type: "wa",
            };
            console.log("Payload:", payload);

            const response = await axiosInstance.post('/api/auth/forgot', payload);

            console.log("Response from /api/auth/forgot:", response.data);
        } catch (err) {
            console.error("Error in sendForgotPasswordRequest:", err.message);
            setError(err.message);
        }
    };


    const verifyPassword = async (password) => {
        try {
            setLoading(true); // Start loading before the request
            console.log('Sending password verification request...', password); // Log just the password value

            const response = await axiosInstance.post('/api/password/verify', { password });

            console.log('Password verification response:', response.data); // Log the response

            // Handle the response based on the status and message
            if (response.status === 200) {  // If the response is 200
                if (response.data.status === 'success' && response.data.message === 'Password is correct!') {
                    setisPasswordFilled(true); // Password is correct, so it's filled
                    setError(null);
                    return true;
                }
            }
        } catch (err) {
            // Handle errors that occur during the request (including status 400 errors)
            if (err.response && err.response.status === 400) {
                if (err.response.data.status === 'error') {
                    if (err.response.data.message === 'Password null.') {
                        setisPasswordFilled(false); // No password is set, so password is not filled
                        setError('Password belum terisi. Mohon isi password di page profile.'); // Error: No password set
                    } else if (err.response.data.message === 'Incorrect password!') {
                        setisPasswordFilled(false); // Password is incorrect, so it's not filled
                        setError('Password salah! Mohon coba kembali.'); // Error: Incorrect password
                    }
                }
            } else {
                // Handle any other errors that occur (e.g., network errors, unexpected errors)
                console.log('Error during password verification:', err.message);
                setError('An error occurred: ' + err.message); // General error message
            }

            return false;
        } finally {
            setLoading(false); // Ensure loading state is turned off after request completes
        }
    };

    const createPaymentRequest = async (data) => {
        try {
            setLoading(true);
            setRequestWDStatus(true); // Mark as loading
            console.log('Payload Payment to API:', JSON.stringify(data, null, 2)); // Log request data
            console.log('Sending payment request with the following data:', data);

            // Send the payment request to the API
            const response = await axiosInstance.post('/api/transaction/payment-request', data);
            console.log('Payment Request API Response:', response);

            // Handle the response based on status
            if (response.data.status === 'success') {
                console.log('Payment request created successfully:', response.data.message); // Corrected spelling error
                setError(null); // Clear any previous error if request is successful
                setRequestWDStatus('beres'); // Mark as successful
            } else if (response.data.status === 'error') {
                console.error('API Error:', response.data.message); // Error message from API
                setError({ message: response.data.message }); // Set error state
                setRequestWDStatus('error'); // Mark as failed
            } else {
                console.error('Unexpected API response structure.');
                setError({ message: 'Unexpected response from the server.' });
                setRequestWDStatus('error'); // Mark as failed
            }
        } catch (err) {
            // Handle errors and check if they are from the API response
            if (err.response) {
                console.error('API Error during createPaymentRequest:', err.response.data.message);
                setError({ message: err.response.data.message }); // Set error state from API message
            } else {
                console.error('Unknown Error during createPaymentRequest:', err.message);
                setError({ message: 'An unknown error occurred.' }); // General error message
            }
            setRequestWDStatus('error'); // Mark as failed in case of an error
        } finally {
            // Reset loading state
            setLoading(false);
            console.log('Loading state set to false');
        }
    };

    const fetchHistoryData = async (startDate, endDate, type) => {
        try {
            setLoading(true);
            setError(null);

            const ref = `${encodeURIComponent(startDate)}_${encodeURIComponent(endDate)}`;
            console.log(`Fetching history data for: ${ref}`);
            console.log(`Fetching history type for: ${type}`);


            const response = await axiosInstance.get(`/api/transaction/history?ref=${ref}&type=${type}`);
            console.log('History API Response:', response.data); // Log the entire API response

            if (response.data.status) {
                setHistoryData(response.data.data);
            } else {
                throw new Error('Invalid response status');
            }
        } catch (err) {
            console.error('Error fetching history data:', err);  // Log the error in detail
            setError(err.response?.data?.message || err.message); // Capture specific API errors
        } finally {
            setLoading(false);
        }
    };

    const fetchScrapeEvents = async () => {
        try {
            setLoading(true); // Start loading state
            const url = 'https://dyandratiket.com/all-events'; // Hardcoded URL
            console.log(`Fetching events from ${url}...`);

            const response = await axiosInstance.get('/api/scrape-events', {
                params: { url }
            });
            console.log("Response received:", response.data);

            if (response.data.status === 'success') {
                const scrapedEvents = response.data.events;
                console.log("Scraped events:", scrapedEvents);

                setEvents(scrapedEvents); // Update the state with scraped events
                return scrapedEvents; // Return the events
            } else {
                const errorMessage = "Failed to fetch events";
                console.error(errorMessage);
                throw new Error(errorMessage); // Trigger error if status is not success
            }
        } catch (err) {
            console.error("Error in fetchScrapeEvents:", err.message);
            setError(err.message); // Update the error state
        } finally {
            setLoading(false); // End loading state
            console.log("fetchScrapeEvents process completed.");
        }

        return []; // Return an empty array if error occurred
    };

    useEffect(() => {
        if (selectedCategory) {
            console.log(`Fetching data for category ${selectedCategory}...`);
            fetchFAQs(selectedCategory);
            fetchPriceList(selectedCategory);
        }
    }, [selectedCategory]);

    return (
        <ContentContext.Provider
            value={{
                userData,
                addresses,
                banner,
                saldoData,
                categories,
                topAgentData,
                DownlineData,
                marketingKitData,
                priceList,
                FAQs,
                selectedCategory,
                loading,
                error,
                SupportKitData,
                BankData,
                isNewUser,
                isPasswordFilled,
                isUserApproved,
                Username,
                RequestWDStatus,
                HistoryData,
                events,
                Notifications,
                userStatus,
                setNotifications,
                setRequestWDStatus,
                setLoading,
                setError,
                setSelectedCategory,
                fetchSaldoData,
                fetchTopAgentData,
                fetchBannerData,
                fetchDownlineData,
                fetchCategoriesData,
                fetchAddressData,
                fetchMarketingKitData,
                fetchFAQs,
                fetchSupportKitData,
                updateBankData,
                fetchBankData,
                verifyPassword,
                sendForgotPasswordBankRequest,
                fetchUserData,
                createPaymentRequest,
                fetchHistoryData,
                updateUserData,
                markNotificationAsRead,
                logoutUser
            }}
        >
            {children}
        </ContentContext.Provider>
    );
}

export function useContent() {
    return useContext(ContentContext);
}
